/**
 * Performance Returns Section Component
 *
 * Displays fund returns over different time periods (1M, 3M, 6M, 1Y, 3Y, 5Y)
 * in a bar chart or table format.
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatPercent } from "@/lib/utils";
import { BarChart3, Table } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

/**
 * Return periods available
 */
export type ReturnPeriod = "1M" | "3M" | "6M" | "1Y" | "3Y" | "5Y";

/**
 * Return data for a single period
 */
export interface PeriodReturnData {
  period: ReturnPeriod;
  fundReturn: number;
  categoryReturn?: number;
  benchmarkReturn?: number;
}

/**
 * Props for the PerformanceReturnsSection component
 */
export interface PerformanceReturnsSectionProps {
  /** Returns data for each period */
  returns: PeriodReturnData[];
  /** Fund name for display */
  fundName?: string;
  /** Benchmark name for display */
  benchmarkName?: string;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const PERIOD_ORDER: ReturnPeriod[] = ["1M", "3M", "6M", "1Y", "3Y", "5Y"];

const PERIOD_LABELS: Record<ReturnPeriod, string> = {
  "1M": "1 Month",
  "3M": "3 Months",
  "6M": "6 Months",
  "1Y": "1 Year",
  "3Y": "3 Years",
  "5Y": "5 Years",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get color class based on return value
 */
function getReturnColor(value: number): string {
  if (value > 0) return "text-green-600 dark:text-green-400";
  if (value < 0) return "text-red-600 dark:text-red-400";
  return "text-muted-foreground";
}

/**
 * Get bar color based on return value
 */
function getBarColor(value: number): string {
  if (value > 0) return "bg-green-500 dark:bg-green-400";
  if (value < 0) return "bg-red-500 dark:bg-red-400";
  return "bg-muted";
}

/**
 * Calculate bar width percentage (max 100%)
 */
function calculateBarWidth(value: number, maxValue: number): number {
  if (maxValue === 0) return 0;
  return Math.min(100, (Math.abs(value) / maxValue) * 100);
}

// ============================================================================
// Subcomponents
// ============================================================================

/**
 * Bar chart view of returns
 */
function BarChartView({
  returns,
  maxReturn,
}: {
  returns: PeriodReturnData[];
  maxReturn: number;
}) {
  return (
    <div className="space-y-3">
      {returns.map((item) => (
        <div key={item.period} className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">
              {PERIOD_LABELS[item.period]}
            </span>
            <span
              className={cn("text-sm font-semibold", getReturnColor(item.fundReturn))}
            >
              {formatPercent(item.fundReturn)}
            </span>
          </div>
          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-300",
                getBarColor(item.fundReturn)
              )}
              style={{ width: `${calculateBarWidth(item.fundReturn, maxReturn)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Table view of returns with comparison
 */
function TableView({
  returns,
  fundName,
  benchmarkName,
}: {
  returns: PeriodReturnData[];
  fundName?: string;
  benchmarkName?: string;
}) {
  const showCategory = returns.some((r) => r.categoryReturn !== undefined);
  const showBenchmark = returns.some((r) => r.benchmarkReturn !== undefined);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border/50">
            <th className="text-left py-2 px-1 font-medium text-muted-foreground">
              Period
            </th>
            <th className="text-right py-2 px-1 font-medium text-muted-foreground">
              {fundName ?? "Fund"}
            </th>
            {showCategory && (
              <th className="text-right py-2 px-1 font-medium text-muted-foreground">
                Category Avg
              </th>
            )}
            {showBenchmark && (
              <th className="text-right py-2 px-1 font-medium text-muted-foreground">
                {benchmarkName ?? "Benchmark"}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {returns.map((item) => (
            <tr
              key={item.period}
              className="border-b border-border/30 last:border-0"
            >
              <td className="py-2.5 px-1 font-medium">
                {PERIOD_LABELS[item.period]}
              </td>
              <td
                className={cn(
                  "py-2.5 px-1 text-right font-semibold",
                  getReturnColor(item.fundReturn)
                )}
              >
                {formatPercent(item.fundReturn)}
              </td>
              {showCategory && (
                <td
                  className={cn(
                    "py-2.5 px-1 text-right",
                    item.categoryReturn !== undefined
                      ? getReturnColor(item.categoryReturn)
                      : "text-muted-foreground"
                  )}
                >
                  {item.categoryReturn !== undefined
                    ? formatPercent(item.categoryReturn)
                    : "-"}
                </td>
              )}
              {showBenchmark && (
                <td
                  className={cn(
                    "py-2.5 px-1 text-right",
                    item.benchmarkReturn !== undefined
                      ? getReturnColor(item.benchmarkReturn)
                      : "text-muted-foreground"
                  )}
                >
                  {item.benchmarkReturn !== undefined
                    ? formatPercent(item.benchmarkReturn)
                    : "-"}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Performance Returns Section
 *
 * Displays fund returns over different time periods with optional
 * category and benchmark comparisons.
 *
 * @example
 * ```tsx
 * <PerformanceReturnsSection
 *   returns={[
 *     { period: "1M", fundReturn: 2.5, categoryReturn: 2.1 },
 *     { period: "3M", fundReturn: 5.8, categoryReturn: 4.9 },
 *     { period: "1Y", fundReturn: 18.5, categoryReturn: 15.2 },
 *     // ...
 *   ]}
 *   fundName="PPFAS Flexi Cap Fund"
 * />
 * ```
 */
export function PerformanceReturnsSection({
  returns,
  fundName,
  benchmarkName,
  className,
}: PerformanceReturnsSectionProps) {
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");

  // Sort returns by period order
  const sortedReturns = [...returns].sort(
    (a, b) => PERIOD_ORDER.indexOf(a.period) - PERIOD_ORDER.indexOf(b.period)
  );

  // Calculate max return for bar chart scaling
  const maxReturn = Math.max(
    ...sortedReturns.map((r) => Math.abs(r.fundReturn)),
    0.01 // Avoid division by zero
  );

  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">Returns</CardTitle>
          <div className="flex items-center gap-1 p-0.5 bg-muted rounded-md">
            <Button
              variant={viewMode === "chart" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setViewMode("chart")}
              aria-label="Bar chart view"
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "table" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setViewMode("table")}
              aria-label="Table view"
            >
              <Table className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {viewMode === "chart" ? (
          <BarChartView returns={sortedReturns} maxReturn={maxReturn} />
        ) : (
          <TableView
            returns={sortedReturns}
            fundName={fundName}
            benchmarkName={benchmarkName}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for PerformanceReturnsSection
 */
export function PerformanceReturnsSectionSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-8 w-16 rounded-md" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {PERIOD_ORDER.map((period) => (
            <div key={period} className="space-y-1">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-12" />
              </div>
              <Skeleton className="h-2 w-full rounded-full" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default PerformanceReturnsSection;
